import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeading, density, radius, typography } from "@aeriosiq/ui";
import { PencilIcon, PlusIcon, Trash2Icon } from "@aeriosiq/ui/icons";

import { useIsMobileViewport } from "../../app/workspaces/useWorkspaces.js";
import { LockStateChip } from "./LockStateChip.js";
import { StandbyTypeChip } from "./StandbyTypeChip.js";
import { TimeTrackingFormDialog } from "./TimeTrackingFormDialog.js";
import {
  formatDateTime,
  formatHours,
  formatTime,
  monthInputValue,
  monthValueToRange,
} from "./format.js";
import {
  useDeleteTimeTrackingMutation,
  useMyTimeTrackingsQuery,
} from "./hooks.js";
import { isLockedConflict } from "./timeTrackingFetch.js";
import {
  STANDBY_TYPES,
  type TimeTracking,
  type TimeTrackingListFilters,
} from "./types.js";

/**
 * TT-02 — Field Data → "Standby & Time Tracking". Mockup screens A (mobile) and
 * B (desktop).
 *
 * ONE route, two layouts. `useIsMobileViewport()` (MUI `md` down) picks card
 * rows or a table; everything else — filters, totals, the form, the mutations —
 * is shared. That is what keeps the two from drifting, and it is why the route
 * is `device: "all"` rather than a separate mobile page.
 *
 * FILTER STATE LIVES IN THE URL (house pattern), so a pilot can bookmark
 * "last month, weather only" and a support conversation can be a link.
 */
export function MyTimeTrackingsPage() {
  const isMobile = useIsMobileViewport();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams],
  );

  const setFilter = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value == null || value === "") next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // TT-05 — "my trackings" spans schemas: a subcontractor pilot's rows live in
  // the project OWNER's schema, so this merges scope=own with scope=member.
  const listQuery = useMyTimeTrackingsQuery(filters);
  const deleteMutation = useDeleteTimeTrackingMutation();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TimeTracking | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const items = listQuery.data?.items ?? [];
  const totals = listQuery.data?.totals;

  const openCreate = useCallback(() => {
    setEditing(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((row: TimeTracking) => {
    setEditing(row);
    setFormOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (row: TimeTracking) => {
      setNotice(null);
      try {
        await deleteMutation.mutateAsync(row.timeTrackingId);
      } catch (error) {
        // The row was locked underneath us — explain rather than fail.
        setNotice(
          isLockedConflict(error)
            ? "That tracking has just been invoiced, so it can no longer be deleted."
            : "Could not delete the time tracking.",
        );
      }
    },
    [deleteMutation],
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 1 }}
      >
        <PageHeading>Standby &amp; time tracking</PageHeading>
        <Button
          variant="contained"
          startIcon={<PlusIcon />}
          onClick={openCreate}
          data-testid="tt-new"
          sx={{ minHeight: density.comfortable.controlHeight, flexShrink: 0 }}
        >
          {isMobile ? "New" : "New time tracking"}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Field Data · My time trackings
      </Typography>

      {notice ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => {
            setNotice(null);
          }}
          data-testid="tt-page-notice"
        >
          {notice}
        </Alert>
      ) : null}

      <Filters filters={filters} onChange={setFilter} />

      {listQuery.isPending ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress aria-label="Loading time trackings" />
        </Stack>
      ) : listQuery.isError ? (
        <Alert severity="error">Could not load your time trackings.</Alert>
      ) : items.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: radius.card }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            No time trackings for this period.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Report weather or technical standby the moment it happens — it is
            the evidence the standby is paid for.
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1} data-testid="tt-card-list">
          {items.map((row) => (
            <TrackingCard
              key={row.timeTrackingId}
              row={row}
              onEdit={openEdit}
              onDelete={handleDelete}
            />
          ))}
        </Stack>
      ) : (
        <TrackingTable
          items={items}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      )}

      {totals ? <Totals totals={totals} /> : null}

      <TimeTrackingFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
        editing={editing}
        fullScreen={isMobile}
      />
    </Box>
  );
}

// ─── Filters ─────────────────────────────────────────────────────────────────

/**
 * Period / type / status, mirrored into the URL. `<input type="month">` rather
 * than a date-range picker: a pilot reads their own time by the month, and the
 * native control is the one that behaves on a phone keyboard.
 */
function Filters({
  filters,
  onChange,
}: {
  filters: TimeTrackingListFilters;
  onChange: (key: string, value: string | null) => void;
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      sx={{ mb: 2 }}
      data-testid="tt-filters"
    >
      <TextField
        label="Period"
        type="month"
        value={monthInputValue(filters.from)}
        onChange={(e) => {
          onChange("month", e.target.value || null);
        }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { "data-testid": "tt-filter-month" },
        }}
        sx={{ minWidth: density.comfortable.controlHeight * 4 }}
      />
      <TextField
        select
        label="Type"
        value={filters.activityType ?? ""}
        onChange={(e) => {
          onChange("type", e.target.value || null);
        }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { "data-testid": "tt-filter-type" },
        }}
        sx={{ minWidth: density.comfortable.controlHeight * 4 }}
      >
        <MenuItem value="">All</MenuItem>
        {STANDBY_TYPES.map((t) => (
          <MenuItem key={t} value={t}>
            {t === "standby_weather" ? "Weather standby" : "Technical standby"}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Status"
        value={
          filters.locked === undefined ? "" : filters.locked ? "locked" : "open"
        }
        onChange={(e) => {
          onChange("status", e.target.value || null);
        }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { "data-testid": "tt-filter-status" },
        }}
        sx={{ minWidth: density.comfortable.controlHeight * 4 }}
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value="open">Editable</MenuItem>
        <MenuItem value="locked">Invoiced</MenuItem>
      </TextField>
    </Stack>
  );
}

// ─── Mobile card ─────────────────────────────────────────────────────────────

function TrackingCard({
  row,
  onEdit,
  onDelete,
}: {
  row: TimeTracking;
  onEdit: (row: TimeTracking) => void;
  onDelete: (row: TimeTracking) => Promise<void>;
}) {
  return (
    <Paper
      variant="outlined"
      data-testid="tt-card"
      data-locked={row.isLocked ? "true" : "false"}
      sx={{
        p: 1.5,
        borderRadius: radius.card,
        // Locked rows read as settled by a muted GROUND, not by opacity:
        // dimming the row composites its text toward the background and drops
        // it under WCAG AA (measured 4.42:1 on the equivalent card in TT-03).
        // The missing edit affordance and the lock chip carry the state.
        backgroundColor: row.isLocked ? "action.hover" : undefined,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <StandbyTypeChip
          activityType={row.activityType}
          suffix={formatHours(row.durationMinutes)}
          dense
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.anchorLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {formatDateTime(row.startTime)} – {formatTime(row.endTime)}
          </Typography>
          {row.description ? (
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {row.description}
            </Typography>
          ) : null}
        </Box>
        {row.isLocked ? (
          <LockStateChip isLocked dense />
        ) : (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              aria-label={`Edit time tracking ${row.anchorLabel}`}
              onClick={() => {
                onEdit(row);
              }}
              sx={{
                width: density.comfortable.controlHeight,
                height: density.comfortable.controlHeight,
              }}
            >
              <PencilIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={`Delete time tracking ${row.anchorLabel}`}
              onClick={() => {
                void onDelete(row);
              }}
              sx={{
                width: density.comfortable.controlHeight,
                height: density.comfortable.controlHeight,
              }}
            >
              <Trash2Icon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

// ─── Desktop table ───────────────────────────────────────────────────────────

function TrackingTable({
  items,
  onEdit,
  onDelete,
}: {
  items: readonly TimeTracking[];
  onEdit: (row: TimeTracking) => void;
  onDelete: (row: TimeTracking) => Promise<void>;
}) {
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: radius.card }}
    >
      <Table size="small" aria-label="My time trackings" data-testid="tt-table">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Type</TableCell>
            <TableCell sx={headCellSx}>Applies to</TableCell>
            <TableCell sx={headCellSx}>Start</TableCell>
            <TableCell sx={headCellSx}>End</TableCell>
            <TableCell sx={headCellSx} align="right">
              Duration
            </TableCell>
            <TableCell sx={headCellSx}>Description</TableCell>
            <TableCell sx={headCellSx}>Status</TableCell>
            <TableCell sx={headCellSx} align="right">
              {/* actions */}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row) => (
            <TableRow
              key={row.timeTrackingId}
              data-testid="tt-row"
              data-locked={row.isLocked ? "true" : "false"}
              // See TrackingCard — a muted ground, never opacity.
              sx={{
                backgroundColor: row.isLocked ? "action.hover" : undefined,
              }}
            >
              <TableCell>
                <StandbyTypeChip activityType={row.activityType} dense />
              </TableCell>
              <TableCell>{row.anchorLabel}</TableCell>
              <TableCell sx={numericSx}>
                {formatDateTime(row.startTime)}
              </TableCell>
              <TableCell sx={numericSx}>{formatTime(row.endTime)}</TableCell>
              <TableCell sx={numericSx} align="right">
                {formatHours(row.durationMinutes)}
              </TableCell>
              <TableCell sx={{ maxWidth: "32ch" }}>
                <Typography
                  variant="body2"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                  title={row.description ?? ""}
                >
                  {row.description ?? "—"}
                </Typography>
              </TableCell>
              <TableCell>
                <LockStateChip isLocked={row.isLocked} dense />
              </TableCell>
              <TableCell align="right">
                {row.isLocked ? null : (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent="flex-end"
                  >
                    <IconButton
                      aria-label={`Edit time tracking ${row.anchorLabel}`}
                      size="small"
                      onClick={() => {
                        onEdit(row);
                      }}
                    >
                      <PencilIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={`Delete time tracking ${row.anchorLabel}`}
                      size="small"
                      onClick={() => {
                        void onDelete(row);
                      }}
                    >
                      <Trash2Icon fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Totals ──────────────────────────────────────────────────────────────────

/**
 * The month footer. These numbers come from the SERVER over the whole filtered
 * set (contract TT-D7) — summing the page would under-report the moment a busy
 * month exceeds one page.
 */
function Totals({
  totals,
}: {
  totals: NonNullable<
    ReturnType<typeof useMyTimeTrackingsQuery>["data"]
  >["totals"];
}) {
  return (
    <Stack
      direction="row"
      spacing={3}
      flexWrap="wrap"
      sx={{ mt: 2 }}
      data-testid="tt-totals"
    >
      <TotalItem label="Total" value={formatHours(totals.totalMinutes)} />
      <TotalItem label="Weather" value={formatHours(totals.weatherMinutes)} />
      <TotalItem
        label="Technical"
        value={formatHours(totals.technicalMinutes)}
      />
      {totals.lockedMinutes > 0 ? (
        <TotalItem label="Invoiced" value={formatHours(totals.lockedMinutes)} />
      ) : null}
    </Stack>
  );
}

function TotalItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        component="div"
        sx={{ ...typography.label, color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ─── URL ↔ filters ───────────────────────────────────────────────────────────

const headCellSx = {
  ...typography.label,
  color: "text.secondary",
  whiteSpace: "nowrap",
} as const;

const numericSx = {
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
} as const;

/**
 * Read the three URL params into a wire filter set.
 *
 * `month=YYYY-MM` rather than `from`/`to`: one param, human-readable in a
 * pasted link, and the `[from,to)` pair is derived. Exported for the unit tier.
 */
export function filtersFromSearchParams(
  params: URLSearchParams,
): TimeTrackingListFilters {
  const filters: TimeTrackingListFilters = { pageSize: 200 };
  const month = params.get("month");
  if (month) {
    const range = monthValueToRange(month);
    if (range) {
      filters.from = range.from;
      filters.to = range.to;
    }
  }
  const type = params.get("type");
  if (type === "standby_weather" || type === "standby_technical") {
    filters.activityType = type;
  }
  const status = params.get("status");
  if (status === "locked") filters.locked = true;
  else if (status === "open") filters.locked = false;
  return filters;
}
